import { AsyncAPIMessage } from 'api/api.types'
import SeamlyGeneralError from 'api/errors/seamly-general-error'
import SeamlyOfflineError from 'api/errors/seamly-offline-error'
import SeamlySessionExpiredError from 'api/errors/seamly-session-expired-error'
import { setHasResponded } from 'domains/app/slice'
import { setLocale } from 'domains/i18n/actions'
import { clearInterrupt, setInterrupt } from 'domains/interrupt/slice'
import { useAppDispatch } from 'domains/store'
import { getConversation } from 'domains/store/actions'
import {
  addEvent,
  clearEvents,
  initResumeConversationPrompt,
  setActiveService,
  setEvents,
  setHeaderSubTitle,
  setIsLoading,
  setParticipant,
  setProactiveMessages,
  setServiceDataItem,
  setServiceEntryMetadata,
  updateEvent,
  updateFeatures,
} from 'domains/store/slice'
import type { ChannelEvent } from 'domains/store/store.types'
import { setTranslationProposalPrompt } from 'domains/translations/slice'
import { useEffect, useRef } from 'preact/hooks'
import useSeamlyEventBusContext from 'ui/hooks/event-bus-hooks'
import {
  useSeamlyCommands,
  useSeamlyIdleDetachCountdown,
  useSeamlyResumeConversationPrompt,
} from 'ui/hooks/seamly-hooks'
import useSeamlyConversation from 'ui/hooks/use-seamly-conversation'

const EMITTABLE_MESSAGE_TYPES = ['text', 'choice_prompt', 'image', 'video']

const SeamlyEventSubscriber = () => {
  const conversation = useSeamlyConversation()
  const messageChannelRef =
    useRef<(_args: { payload: any; type: string }) => Promise<void>>()
  const eventRef = useRef<(_event: AsyncAPIMessage) => AsyncAPIMessage | void>()
  const dispatch = useAppDispatch()
  const eventBus = useSeamlyEventBusContext()
  const prevEmittedEventId = useRef(null)
  const { initCountdown, endCountdown } = useSeamlyIdleDetachCountdown()
  const { emitEvent } = useSeamlyCommands()
  const { hasPrompt, continueChat } = useSeamlyResumeConversationPrompt()
  const hasResumeConversationPrompt = useRef(hasPrompt)

  useEffect(() => {
    hasResumeConversationPrompt.current = hasPrompt
  }, [hasPrompt])

  useEffect(() => {
    if (conversation.socket) {
      conversation.onConnection(({ currentState }) => {
        if (currentState === 'attach_channel_erred') {
          const seamlyGeneralError = new SeamlyGeneralError()
          dispatch(
            setInterrupt({
              name: seamlyGeneralError.name,
              message: seamlyGeneralError.message,
              langKey: seamlyGeneralError.langKey,
              originalEvent: seamlyGeneralError.originalEvent,
              originalError: seamlyGeneralError.originalError,
              action: seamlyGeneralError.action,
            }),
          )

          return true
        }

        return false
      })

      conversation.onError((err) => {
        const seamlyOfflineError = new SeamlyOfflineError(err)
        dispatch(
          setInterrupt({
            name: seamlyOfflineError.name,
            message: seamlyOfflineError.message,
            langKey: seamlyOfflineError.langKey,
            originalEvent: seamlyOfflineError.originalEvent,
            originalError: seamlyOfflineError.originalError,
          }),
        )

        dispatch(clearEvents())
      })

      conversation.onOpen(() => {
        dispatch(clearInterrupt())
      })
    }
  }, [conversation, conversation.socket, dispatch])

  useEffect(() => {
    conversation.onConnection(({ connected }) => {
      if (!connected) return false

      if (eventRef.current) {
        conversation?.removeListener('message', eventRef.current)
      }

      const updateParticipant = (event) => {
        const { payload } = event
        if (!payload || !payload.participant) {
          return
        }

        const { fromClient, participant } = payload

        if (
          !fromClient &&
          typeof participant !== 'string' &&
          participant?.name
        ) {
          dispatch(setHeaderSubTitle(participant.name))
        }

        dispatch(setParticipant(payload))

        if (typeof participant !== 'string' && participant.introduction) {
          dispatch(
            addEvent({
              ...event,
              payload: {
                ...payload,
                type: 'participant',
              },
            }),
          )
        }
      }

      eventRef.current = (event: AsyncAPIMessage) => {
        switch (event.type) {
          case 'ui':
            switch (event.payload.type) {
              case 'idle_detach_countdown':
                if (event.payload.body.type === 'idle_detach_countdown') {
                  initCountdown(event.payload.body.duration)
                }
                break

              case 'loading':
                // @ts-ignore
                dispatch(setIsLoading(event.payload.body.loading))
                break
              case 'idle_detach_countdown_elapsed':
                endCountdown(undefined, true)
                break
              case 'resume_conversation_prompt':
                dispatch(initResumeConversationPrompt())
                break
              case 'user_first_response':
                dispatch(setHasResponded(true))
                emitEvent('system.userFirstResponse', event.payload.body)
                break
              // @ts-ignore
              case 'translation_proposal':
                // @ts-ignore
                dispatch(setTranslationProposalPrompt(event.payload.body))
                break
            }
            break
          case 'service_attach': {
            const {
              service: {
                name,
                settings: {
                  proactiveMessages: { enabled },
                  entry,
                },
                sessionId,
              },
            } = event.payload

            dispatch(setProactiveMessages(enabled || false))
            if (name) emitEvent('system.serviceChanged', name)
            if (sessionId) dispatch(setActiveService(sessionId))
            dispatch(setServiceEntryMetadata(entry))
            dispatch(
              updateFeatures({
                uploads: { enabled: entry.options?.upload?.enabled || false },
              }),
            )
            break
          }
          case 'context':
            if (event.payload.contentLocale && event.payload.userLocale) {
              dispatch(setLocale(event.payload.userLocale))
            }
            break
          case 'message':
            switch (event.payload.type) {
              case 'text':
              case 'choice_prompt':
              case 'splash':
              case 'image':
              case 'upload':
              case 'video':
              case 'custom':
              case 'carousel':
              case 'card':
                if (
                  event.payload.service &&
                  event.payload.service.serviceSessionId
                ) {
                  dispatch(
                    setActiveService(event.payload.service.serviceSessionId),
                  )
                }

                if (event.payload.fromClient) {
                  dispatch(updateEvent(event as unknown as ChannelEvent))
                  break
                }

                if (hasResumeConversationPrompt.current) {
                  continueChat()
                }

                dispatch(addEvent(event as unknown as ChannelEvent))

                break
            }
            break
          case 'participant':
            updateParticipant(event)
            break
          case 'service_data':
            if (event.payload.persist) {
              dispatch(setServiceDataItem(event.payload))
            }
            break
          case 'info':
            if (
              event.payload.type === 'divider' ||
              event.payload.type === 'text' ||
              event.payload.type === 'translation'
            ) {
              dispatch(
                addEvent({
                  type: 'info',
                  payload: {
                    ...event.payload,
                    participant: null,
                  },
                }),
              )
            }
            break
          case 'error':
            switch (event.payload.type) {
              case 'find_conversation': {
                const seamlySessionExpiredError = new SeamlySessionExpiredError(
                  event,
                )
                dispatch(
                  setInterrupt({
                    name: seamlySessionExpiredError.name,
                    action: seamlySessionExpiredError.action,
                    message: seamlySessionExpiredError.message,
                    originalEvent: seamlySessionExpiredError.originalEvent,
                    originalError: seamlySessionExpiredError.originalError,
                  }),
                )
                break
              }
              case 'conversation': {
                const seamlyGeneralError = new SeamlyGeneralError(event)
                dispatch(
                  setInterrupt({
                    name: seamlyGeneralError.name,
                    message: seamlyGeneralError.message,
                    langKey: seamlyGeneralError.langKey,
                    originalEvent: seamlyGeneralError.originalEvent,
                    originalError: seamlyGeneralError.originalError,
                    action: seamlyGeneralError.action,
                  }),
                )
                break
              }
            }
        }

        return event
      }
      conversation.onMessage(eventRef.current)

      return true
    })
  }, [
    continueChat,
    conversation,
    dispatch,
    emitEvent,
    endCountdown,
    eventBus,
    initCountdown,
  ])

  useEffect(() => {
    conversation.onConnection(({ connected }) => {
      if (!connected) return false

      if (messageChannelRef.current) {
        conversation.removeListener('message', messageChannelRef.current)
      }

      messageChannelRef.current = async ({ payload }) => {
        if (payload?.type === 'history_changed') {
          try {
            const events = await dispatch(getConversation(payload)).unwrap()
            if (events && 'service' in events) {
              dispatch(setEvents(events))
            }
          } catch (_e) {
            // nothing to do,
          }
        }

        if (!EMITTABLE_MESSAGE_TYPES.includes(payload?.type)) {
          return payload
        }

        // This check dedupes the sending of messages via
        // the bus if a duplicate connection exists in an
        // error situation.
        if (payload.id !== prevEmittedEventId.current) {
          // @ts-ignore
          eventBus.emit('message', payload)
        }
        prevEmittedEventId.current = payload.id
        return payload
      }

      conversation.addListener('message', messageChannelRef.current)

      return true
    })
  }, [conversation, dispatch, eventBus])

  return null
}

export default SeamlyEventSubscriber
